import { useEffect, useState } from 'react';

import { NOT_ALLOWED_PAYMENT_TYPES } from '@/lib/billing/payment-method-capabilities';
import type { PaymentMethod } from '@/pages/cms/billing/types';

interface UseSelectedPaymentMethodOptions {
  paymentMethods: PaymentMethod[];
  resetOn: boolean;
  requireImmediatePayment?: boolean;
}

/** Selects the customer's default payment method, falling back to the first one available. */
export function useSelectedPaymentMethod({
  paymentMethods,
  resetOn,
  requireImmediatePayment,
}: UseSelectedPaymentMethodOptions) {
  const selectablePaymentMethods = requireImmediatePayment
    ? paymentMethods.filter(
        (paymentMethod) => !NOT_ALLOWED_PAYMENT_TYPES.includes(paymentMethod.type),
      )
    : paymentMethods;
  const defaultPaymentMethod = selectablePaymentMethods.find(
    (paymentMethod) => paymentMethod.default,
  );
  const fallbackId = defaultPaymentMethod?.id ?? selectablePaymentMethods[0]?.id ?? '';
  const [selectedId, setSelectedId] = useState(fallbackId);

  // biome-ignore lint/correctness/useExhaustiveDependencies: selectablePaymentMethods is a fresh array each render; fallbackId (derived from it) is the stable dependency
  useEffect(() => {
    if (!resetOn) return;

    setSelectedId((current) =>
      selectablePaymentMethods.some((paymentMethod) => paymentMethod.id === current)
        ? current
        : fallbackId,
    );
  }, [resetOn, fallbackId]);

  return [selectedId, setSelectedId, selectablePaymentMethods] as const;
}
